import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {ConsiderDocument} from "../../generated-model/model";
import {environment} from "../../environments/environment";
import {saveAs} from "file-saver";
import {map, Observable} from "rxjs";

/** รูปที่ viewer ยอมให้ browser render — ไม่มี image/svg+xml เพราะ SVG มีสคริปต์ได้ */
const VIEWABLE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/bmp'];

export type DocumentViewer = 'pdf' | 'image';

@Injectable({providedIn: "root"})
export class ConsiderDocumentResource {
  http = inject(HttpClient);

  downloadDoc(file: ConsiderDocument) {
    this.http.get(this.buildUrl(file), {responseType: "blob"}).subscribe(data => {
      saveAs(new Blob([data]), file.fileName);
    })
  }

  /**
   * โหลดไฟล์ผ่าน HttpClient (แนบ bearer token) แล้วคืน object URL สำหรับ <img src> / <object data>
   * — ให้ <img>/<object> โหลดจาก URL ของ endpoint ตรงๆ ไม่ได้ เพราะ endpoint ต้อง login แต่ request ของ browser ไม่มี token
   * ผู้เรียกต้อง URL.revokeObjectURL เมื่อปิด viewer
   *
   * ชนิดของ blob มาจาก viewer ที่จะแสดง ไม่ใช่ mimeType ที่เก็บไว้ (ผู้อัปโหลดกำหนดเอง): blob: URL มี origin เดียวกับ SRM
   * ถ้า mimeType เป็น text/html (เช่น "text/html;pdf" ซึ่งผ่าน isPdf) หรือ image/svg+xml สคริปต์ในไฟล์จะรันในหน้า SRM ได้
   */
  objectUrl(file: ConsiderDocument, viewer: DocumentViewer): Observable<string> {
    const type = viewer === 'pdf' ? 'application/pdf' : viewableImageType(file.mimeType);
    return this.http.get(this.buildUrl(file), {responseType: "blob"})
      .pipe(map(blob => URL.createObjectURL(new Blob([blob], {type}))));
  }

  private buildUrl(file: ConsiderDocument): string {
    const filePathSegment = file.filePath ? `${file.filePath}` : `/${file.bucket}`;
    return `${environment.serverUrl}/public/api/consider-document/download/${file.storageRepoKey}${filePathSegment}/${file.fileName}`;
  }
}

/** ชนิดรูปที่อยู่ใน allowlist (ตัด parameter เช่น ";charset=" ออก) นอกนั้นเป็น octet-stream — <img> ยังแสดงรูป raster ได้ */
function viewableImageType(mimeType?: string): string {
  const base = (mimeType ?? '').split(';')[0].trim().toLowerCase();
  return VIEWABLE_IMAGE_TYPES.includes(base) ? base : 'application/octet-stream';
}
